import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, Loader2, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sectionLabels = {
  transporters: 'privateTransporters',
  purchases: 'privatePurchases',
  ecommerce: 'privateEcommerce',
  clients: 'privateClients',
};

export const PrivateLogin = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section') || 'clients';
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/private/login`, {
        username,
        password,
        user_type: section,
      });

      if (response.data.success) {
        // Store user in sessionStorage
        sessionStorage.setItem('privateUser', JSON.stringify(response.data.user));
        toast.success(`${t('welcome')}, ${response.data.user.username}!`);
        
        // Redirect to portal
        navigate(`/private/portal?section=${section}`);
      }
    } catch (error) {
      toast.error(t('loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4" data-testid="private-login-page">
      <div className="w-full max-w-md">
        <div className="bg-white border border-slate-200 shadow-lg">
          {/* Header */}
          <div className="bg-red-700 text-white p-6 text-center">
            <Lock className="h-12 w-12 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
              {t('privateLogin')}
            </h1>
            <p className="text-red-200 mt-2">{t('privateLoginSubtitle')}</p>
          </div>

          {/* Section Badge */}
          <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
            <div className="text-center">
              <span className="text-sm text-slate-500">{t('userType')}:</span>
              <span className="ml-2 font-bold text-slate-900 uppercase">
                {t(sectionLabels[section])}
              </span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="p-6 space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium text-slate-700">
                {t('username')}
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="rounded-none border-slate-300"
                data-testid="input-username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                {t('password')}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-none border-slate-300"
                data-testid="input-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-red-700 hover:bg-red-800 text-white rounded-none uppercase tracking-wider font-bold py-3"
              data-testid="login-button"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                t('login')
              )}
            </Button>
          </form>

          {/* Back to Home */}
          <div className="px-6 pb-6">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="w-full rounded-none border-slate-300"
              data-testid="back-home"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('home')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
